import type { Addresses } from 'api/types/addresses.types';
import { useConfig } from 'context/app-config';
import useFlags from 'hooks/useFlags';
import { addressAdded, selectAddress } from 'pages/newOrder/newOrder.slice';
import { useAddCustomerAddress } from 'pages/queries/address.queries';
import { usePickupTimeSlotsMutation } from 'pages/queries/timeslot.queries';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { AppDispatch } from 'redux-stores/store';
import type { AddressForm } from 'schemas/address-schema';
import { notifyErrorObject } from 'wrappers/reporting';

import { AddressFormPage } from '../components/AddressFormPage';
import { AddressGeofence } from '../components/AddressGeoFence';
import { AddressSearch } from '../components/AddressSearch';
import { AddressType } from '../components/AddressType';

export interface IAddAddressProps {}

const AddAddress: React.FC<IAddAddressProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const config = useConfig();
  const { enableSelectServicesFlow } = useFlags();

  const { mutateAsync } = usePickupTimeSlotsMutation();

  const handleRouting = async (address: Addresses.Address) => {
    if (config?.modules.newOrder.steps.pickAddressOnAddStep) {
      dispatch(selectAddress(address.id));
      if (config?.modules.newOrder.address.showStep) {
        await mutateAsync({
          addressId: address.id,
        });
      }
    }
  };

  const { mutateAsync: addCustomerAddress, isLoading } = useAddCustomerAddress({
    onSuccess: async (data) => {
      if (config?.api?.addAddress) {
        dispatch(addressAdded(data));
        await handleRouting(data.address);
        const route = enableSelectServicesFlow ? '/newOrder/flex?step=1' : '/newOrder';
        navigate(route, { replace: true });
      } else {
        navigate('/newOrder', { replace: true });
      }
    },
    onError: (error) => {
      notifyErrorObject(error);
      toast.dark(t('common.addressAddedFailed'));
    },
  });

  const handleAddAddress = useCallback(
    async (addressForm: AddressForm) => {
      await addCustomerAddress(addressForm);
    },
    [addCustomerAddress]
  );

  return (
    <Routes>
      <Route path="addressGeofence" element={<AddressGeofence />} />
      <Route path="addressType" element={<AddressType />} />
      <Route path="addressFormPage" element={<AddressFormPage isLoading={isLoading} onSubmit={handleAddAddress} />} />
      <Route index element={<AddressSearch />} />
    </Routes>
  );
};

export default AddAddress;
